import {
  get,
  post,
  put,
  getBudgetReconcileData
} from "../../network/apiService";
import { getBudgetData } from "../budgetReview/budgetReview.action";
import { apiUrlHost } from "../servicerOnboarding/servicerOnboarding.action";

const ELIGIBLE_DRAW_STAGES = new Set([
  "Wire Completed",
  "Wire Transfer Initiated"
]);

const QUALIFYING_INSPECTION_STATUSES = new Set([
  "Approved",
  "Approved Conditionally",
  "Completed"
]);

const emptyProgressLineItem = (lineItem: any) => ({
  item: lineItem.item,
  desc: lineItem.desc,
  amount: "-",
  completion: "-"
});

const toReconcileItemMap = (reconcileData: any[] = []) => {
  const itemMap = new Map<string, any>();
  reconcileData.forEach((item: any) => {
    itemMap.set(item.budgetLineItem.item.trim(), item);
  });
  return itemMap;
};

const findMatchingReconcileItem = (
  reconciledItems: any[],
  lineItemName: string
) =>
  reconciledItems.find(
    (item: any) => item.budgetLineItem.item.trim() === lineItemName.trim()
  );

const buildQualifyingProgressLineItem = (lineItem: any, matchingItem: any) => {
  const approvedLatestCompletion =
    Number(matchingItem.approvedLatestCompletion) || 0;
  const approvedAmount = Number(matchingItem.approvedAmount);
  const approvedDrawAmount = Number.isFinite(approvedAmount)
    ? approvedAmount.toFixed(2)
    : "-";

  return {
    item: lineItem.item,
    desc: lineItem.desc,
    amount: approvedDrawAmount,
    completion: approvedLatestCompletion.toFixed(2)
  };
};

const buildFirstDrawDerivedLineItem = (
  lineItem: any,
  nextQualifyingDrawData: Map<string, any>
) => {
  const nextDrawItem = nextQualifyingDrawData.get(lineItem.item.trim());
  if (!nextDrawItem) return null;

  const financedBudget =
    Number(nextDrawItem.budgetLineItem?.financedBudget) || 0;
  const nextDrawLatestCompletion =
    Number(nextDrawItem.previousCompletion) || 0;
  const amount = ((financedBudget * nextDrawLatestCompletion) / 100).toFixed(2);

  return {
    item: lineItem.item,
    desc: lineItem.desc,
    amount,
    completion: nextDrawLatestCompletion.toFixed(2)
  };
};

const buildSandwichedDerivedLineItem = (
  lineItem: any,
  previousDrawData: Map<string, any>,
  nextQualifyingDrawData: Map<string, any>
) => {
  const nextDrawItem = nextQualifyingDrawData.get(lineItem.item.trim());
  const prevDrawItem = previousDrawData.get(lineItem.item.trim());
  if (!nextDrawItem || !prevDrawItem) return null;

  const financedBudget =
    Number(nextDrawItem.budgetLineItem?.financedBudget) || 0;
  const nextDrawLatestCompletion =
    Number(nextDrawItem.previousCompletion) || 0;
  const prevDrawLatestCompletion =
    Number(prevDrawItem.approvedLatestCompletion) || 0;
  const amount = (
    (financedBudget * (nextDrawLatestCompletion - prevDrawLatestCompletion)) /
    100
  ).toFixed(2);

  return {
    item: lineItem.item,
    desc: lineItem.desc,
    amount,
    completion: nextDrawLatestCompletion.toFixed(2)
  };
};

const buildProgressLineItem = ({
  lineItem,
  drawIndex,
  isQualifying,
  hasData,
  reconciledItems,
  nextQualifyingDrawData,
  previousDrawData
}: {
  lineItem: any;
  drawIndex: number;
  isQualifying: boolean;
  hasData: boolean;
  reconciledItems: any[];
  nextQualifyingDrawData: Map<string, any> | null;
  previousDrawData: Map<string, any> | null;
}) => {
  const matchingItem = hasData
    ? findMatchingReconcileItem(reconciledItems, lineItem.item)
    : null;

  if (isQualifying && hasData && matchingItem) {
    return buildQualifyingProgressLineItem(lineItem, matchingItem);
  }

  if (drawIndex === 0 && !isQualifying && nextQualifyingDrawData) {
    const derived = buildFirstDrawDerivedLineItem(
      lineItem,
      nextQualifyingDrawData
    );
    if (derived) return derived;
  }

  if (
    drawIndex > 0 &&
    !isQualifying &&
    previousDrawData &&
    nextQualifyingDrawData
  ) {
    const derived = buildSandwichedDerivedLineItem(
      lineItem,
      previousDrawData,
      nextQualifyingDrawData
    );
    if (derived) return derived;
  }

  return emptyProgressLineItem(lineItem);
};

const getAdjacentReconcileMaps = ({
  isQualifying,
  drawIndex,
  allDraws,
  drawDataMap
}: {
  isQualifying: boolean;
  drawIndex: number;
  allDraws: any[];
  drawDataMap: Map<string, any>;
}) => {
  let nextQualifyingDrawData: Map<string, any> | null = null;
  let previousDrawData: Map<string, any> | null = null;

  if (!isQualifying && drawIndex + 1 < allDraws.length) {
    const nextDrawData = drawDataMap.get(
      allDraws[drawIndex + 1].drawId.toString()
    );
    if (nextDrawData?.length > 0) {
      nextQualifyingDrawData = toReconcileItemMap(nextDrawData);
    }
  }

  if (!isQualifying && drawIndex > 0) {
    const prevReconcileData = drawDataMap.get(
      allDraws[drawIndex - 1].drawId.toString()
    );
    if (prevReconcileData?.length > 0) {
      previousDrawData = toReconcileItemMap(prevReconcileData);
    }
  }

  return { nextQualifyingDrawData, previousDrawData };
};

const updateInspectionInfoForDraw = ({
  reconciledItems,
  lineItemsMap,
  drawId,
  isQualifying,
  hasData
}: {
  reconciledItems: any[];
  lineItemsMap: Map<number, any>;
  drawId: string;
  isQualifying: boolean;
  hasData: boolean;
}) => {
  reconciledItems.forEach((item: any) => {
    const lineItemId = item.budgetLineItemsId;
    if (!lineItemsMap.has(lineItemId)) return;

    const lineItem = lineItemsMap.get(lineItemId)!;
    lineItem.inspectionInfo[drawId] = {
      latestCompletion:
        isQualifying && hasData ? item.approvedLatestCompletion : "-",
      latestInspectionBudget: item.budgetLineItem?.financedBudget,
      previousCompletion: item.previousCompletion
    };
  });
};

const processDrawProgressEntry = ({
  draw,
  drawIndex,
  allDraws,
  qualifyingDraws,
  drawDataMap,
  lineItemsMap
}: {
  draw: any;
  drawIndex: number;
  allDraws: any[];
  qualifyingDraws: any[];
  drawDataMap: Map<string, any>;
  lineItemsMap: Map<number, any>;
}) => {
  const drawId = draw.drawId.toString();
  const isQualifying = qualifyingDraws.some(
    (d: any) => d.drawId === draw.drawId
  );
  const reconciledItems = isQualifying ? drawDataMap.get(drawId) || [] : [];
  const hasData = reconciledItems.length > 0;
  const { nextQualifyingDrawData, previousDrawData } = getAdjacentReconcileMaps({
    isQualifying,
    drawIndex,
    allDraws,
    drawDataMap
  });

  const isDerivedFromAdjacent =
    !isQualifying &&
    ((drawIndex === 0 && !!nextQualifyingDrawData) ||
      (drawIndex > 0 && !!previousDrawData && !!nextQualifyingDrawData));

  const lineItems = Array.from(lineItemsMap.values()).map((lineItem: any) =>
    buildProgressLineItem({
      lineItem,
      drawIndex,
      isQualifying,
      hasData,
      reconciledItems,
      nextQualifyingDrawData,
      previousDrawData
    })
  );

  updateInspectionInfoForDraw({
    reconciledItems,
    lineItemsMap,
    drawId,
    isQualifying,
    hasData
  });

  return {
    drawId,
    isDerivedFromAdjacent,
    lineItems
  };
};

export const getLoanInfoForPL = (loanId: string) => {
  const url = `${apiUrlHost}/purchasedloans/aggregate/loanInfo?toorakLoanId=${loanId}`;
  return get(url);
};

export const updatePurchaseFlags = (requestBody: any) => {
  const url = `${apiUrlHost}/drawservice/assetmanagement/document/flag`;
  return put(url, requestBody);
};

export const updateDrawDocsTags = (documentId: string, requestBody: any[]) => {
  const url = `${apiUrlHost}/drawservice/assetmanagement/document/${documentId}/tags`;
  return put(url, requestBody);
};

export const updateDrawDocsAssets = (requestBody: any[], documentId: any) => {
  let url = `${apiUrlHost}/drawservice/assetmanagement/document/assets`;
  if (!requestBody || requestBody.length === 0) {
    url += `?documentId=${documentId}`;
  }
  return put(url, requestBody);
};

export const updateDocsReviewStatus = (requestBody: any) => {
  const url = `${apiUrlHost}/purchasedloans/updateStatus`;
  return post(url, requestBody);
};

export const transferDocs = (documentId: string, requestBody: any) => {
  const url = `${apiUrlHost}/documentdetails/${documentId}/transfer`;
  return put(url, requestBody);
};

export const importDocs = (toorakLoanId: string, requestBody: any) => {
  const url = `${apiUrlHost}/purchasedloans/loan/${toorakLoanId}/import`;
  return put(url, requestBody);
};

export const fetchDrawProgress = (toorakLoanId: string, requiredDraw: any) => {
  return async (dispatch: any) => {
    try {
      // Step 1: Call getBudgetData through Redux dispatch to get line items
      let budgetDataResponse: any = null;

      // Create a wrapper promise that resolves when getBudgetData callback is called
      const budgetDataPromise = new Promise<void>((resolve) => {
        const thunkAction = getBudgetData(
          toorakLoanId,
          0,
          (_selectedDocId: any, _budgetLines: any, response: any) => {
            budgetDataResponse = response;
            resolve();
          }
        );
        dispatch(thunkAction);
      });

      await budgetDataPromise;

      // Step 2: Check if budget approval status is "Approved"
      const budgetApprovalStatus = budgetDataResponse?.budgetApprovalStatus;
      if (budgetApprovalStatus !== "Approved") {
        // Only return empty if budget is NOT approved
        return { lineItems: [], draws: [], approved: false };
      }

      // Extract line items from budget data (if available)
      const budgetLineItems =
        budgetDataResponse?.budgetDocumentInfoList?.[0]?.budgetLineItemList ||
        [];

      // Step 3: Extract line items from budget data
      const lineItemsMap = new Map<number, any>();
      budgetLineItems.forEach((item: any) => {
        lineItemsMap.set(item.budgetLineItemId, {
          item: item.item || "",
          description: item.description || "",
          desc: item.description || "",
          budget: item.budget || "",
          financedBudget: item.financedBudget || "",
          inspectionInfo: {}
        });
      });

      // If no line items, return empty line items but with approved flag
      if (budgetLineItems.length === 0) {
        const lineItems = Array.from(lineItemsMap.values());
        return { lineItems, draws: [], approved: true };
      }

      // Step 4: Only Wire Completed / Wire Transfer Initiated draws are eligible.
      // budgetreconcile-backed progress (non-derived) further requires inspection
      // completion statuses below.
      const allDraws =
        requiredDraw?.allDraws?.filter(
          (draw: any) =>
            draw?.drawNumber !== undefined &&
            draw?.drawNumber !== null &&
            ELIGIBLE_DRAW_STAGES.has(draw?.stage)
        ) || [];

      if (allDraws.length === 0) {
        const lineItems = Array.from(lineItemsMap.values());
        return { lineItems, draws: [], approved: true };
      }

      // Sort draws by drawNumber
      allDraws.sort(
        (a: any, b: any) => Number(a.drawNumber) - Number(b.drawNumber)
      );

      // Call budgetreconcile only for qualifying draws; derived adjacent fills
      // cover non-qualifying draws without hitting that API.
      const qualifyingDraws = allDraws.filter(
        (draw: any) =>
          draw?.budgetReconcileStatus === "Completed" &&
          QUALIFYING_INSPECTION_STATUSES.has(draw?.inspectionReviewStatus)
      );

      // Call budget reconcile endpoint only for qualifying draws
      const drawDataPromises = qualifyingDraws.map((draw: any) =>
        getBudgetReconcileData(draw.drawId).catch((err: any) => {
          console.error(
            `Error fetching budget reconcile for draw ${draw.drawId}:`,
            err
          );
          return { data: [] };
        })
      );

      const qualifyingDrawDataResponses = await Promise.all(drawDataPromises);

      // Create a map of draw data for quick lookup
      const drawDataMap = new Map<string, any>();
      qualifyingDraws.forEach((draw: any, idx: number) => {
        const drawId = draw.drawId.toString();
        drawDataMap.set(drawId, qualifyingDrawDataResponses[idx]?.data || []);
      });

      const processedDraws = allDraws.map((draw: any, drawIndex: number) =>
        processDrawProgressEntry({
          draw,
          drawIndex,
          allDraws,
          qualifyingDraws,
          drawDataMap,
          lineItemsMap
        })
      );

      const lineItems = Array.from(lineItemsMap.values());
      return { lineItems, draws: processedDraws, approved: true };
    } catch (err) {
      console.error(err);
      return { lineItems: [], draws: [], approved: false };
    }
  };
};

export const startTaggingDocs = (toorakLoanId: string) => {
  const url = `${apiUrlHost}/purchasedloans/loan/${toorakLoanId}/tag`;
  return put(url);
};
